/**
 * Transfers Page
 *
 * Main page for transfers management.
 * @see Sprint 14 - Transferts Inter-Sites
 * @see US-14.5 - UI Liste et Creation
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeftRight, Plus } from 'lucide-react';
import { fetchAllForExport } from '@bill/ui';
import { Button, Skeleton, EmptyState } from '@/components/ui';
import { useDrawerRoute, useDebounce, useBrandContext, usePermission } from '@/hooks';
import { useTransfers } from '../hooks';
import { transfersService } from '../services/transfers.service';
import {
  TransfersKPIs,
  TransfersFilters,
  TransfersTable,
  TransferDetailDrawer,
  CreateTransferModal,
} from '../components';
import type { TransferListItem, QueryTransfersDto, TransferStatus } from '@bstock/shared';

type SortField = QueryTransfersDto['sortBy'];
type SortOrder = QueryTransfersDto['sortOrder'];
type RoleType = 'my_requests' | 'to_process';

const DEFAULT_LIMIT = 20;

/**
 * Parse URL params for deep linking
 */
function parseUrlParams(searchParams: URLSearchParams): {
  role: RoleType;
  status: string;
  search: string;
  page: number;
  sortBy: SortField;
  sortOrder: SortOrder;
} {
  const role = searchParams.get('role') as RoleType | null;
  return {
    role: role === 'my_requests' || role === 'to_process' ? role : 'my_requests',
    status: searchParams.get('status') ?? '',
    search: searchParams.get('search') ?? '',
    page: parseInt(searchParams.get('page') ?? '1', 10),
    sortBy: (searchParams.get('sortBy') as SortField) ?? 'createdAt',
    sortOrder: (searchParams.get('sortOrder') as SortOrder) ?? 'desc',
  };
}

export default function TransfersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSite, isCentral, brandId } = useBrandContext();
  const currentSiteId = (isSite || isCentral) && brandId ? brandId : undefined;

  // Permissions
  const canCreate = usePermission('transfers.create');
  const canShip = usePermission('transfers.ship');
  const canReceive = usePermission('transfers.receive');
  const canDelete = usePermission('transfers.delete');

  // Parse URL params for deep linking
  const urlParams = useMemo(() => parseUrlParams(searchParams), [searchParams]);

  // Drawer routing with deep linking
  const drawer = useDrawerRoute('/stock/transferts', { paramName: 'transferId' });

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);

  // La centrale a desormais 2 flux : PO entrants (elle expedie, role='to_process')
  // et retours fournisseurs entrants (elle receptionne, role='my_requests' car
  // destination du Transfer miroir). Donc meme structure d'onglets qu'un site
  // normal — on ne force plus 'to_process'.
  const [role, setRole] = useState<RoleType>(urlParams.role);
  const [statusFilter, setStatusFilter] = useState(urlParams.status);
  const [searchQuery, setSearchQuery] = useState(urlParams.search);
  const [page, setPage] = useState(urlParams.page);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [sortBy, setSortBy] = useState<SortField>(urlParams.sortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(urlParams.sortOrder);

  // Debounce search
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Update URL when filters change
  const updateUrl = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Query params
  const queryParams = useMemo((): QueryTransfersDto => ({
    page,
    limit,
    role,
    status: statusFilter ? (statusFilter as TransferStatus) : undefined,
    search: debouncedSearch || undefined,
    sortBy,
    sortOrder,
  }), [page, limit, role, statusFilter, debouncedSearch, sortBy, sortOrder]);

  // Fetch transfers
  const { data: transfersData, isLoading } = useTransfers(queryParams);

  const transfers = transfersData?.data ?? [];
  const pagination = transfersData?.pagination;

  // Handlers
  const handleRowClick = useCallback(
    (transfer: TransferListItem) => {
      drawer.open(transfer.id);
    },
    [drawer]
  );

  const handleSort = useCallback((field: SortField, order: SortOrder) => {
    setSortBy(field);
    setSortOrder(order);
    setPage(1);
    updateUrl({ sortBy: field || null, sortOrder: order || null, page: null });
  }, [updateUrl]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    updateUrl({ page: String(newPage) });
  }, [updateUrl]);

  const handleLimitChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
    updateUrl({ page: null });
  }, [updateUrl]);

  const handleRoleChange = useCallback((newRole: RoleType) => {
    setRole(newRole);
    setPage(1);
    updateUrl({ role: newRole || null, page: null });
  }, [updateUrl]);

  const handleStatusChange = useCallback((newStatus: string) => {
    setStatusFilter(newStatus);
    setPage(1);
    updateUrl({ status: newStatus || null, page: null });
  }, [updateUrl]);

  const handleSearchChange = useCallback((newSearch: string) => {
    setSearchQuery(newSearch);
    setPage(1);
    updateUrl({ search: newSearch || null, page: null });
  }, [updateUrl]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <ArrowLeftRight className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-dark-500">Transferts</h1>
              <p className="text-sm text-neutral-500 mt-0.5">
                Gestion des transferts inter-sites
              </p>
            </div>
          </div>
{canCreate && !isCentral && (
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle demande
            </Button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <TransfersKPIs />
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-neutral-200 px-6 py-3 flex-shrink-0">
        <TransfersFilters
          role={role}
          status={statusFilter}
          search={searchQuery}
          onRoleChange={handleRoleChange}
          onStatusChange={handleStatusChange}
          onSearchChange={handleSearchChange}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-neutral-50">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : transfers.length === 0 ? (
          <EmptyState
            icon={ArrowLeftRight}
            title="Aucun transfert"
            description={isCentral
              ? "Les commandes des restaurants apparaitront ici"
              : "Creez une nouvelle demande de transfert pour approvisionner votre site"
            }
            action={canCreate && !isCentral ? {
              label: 'Nouvelle demande',
              onClick: () => setShowCreateModal(true),
              icon: Plus,
            } : undefined}
          />
        ) : (
          <TransfersTable
            transfers={transfers}
            pagination={pagination ?? { page: 1, limit, total: 0, totalPages: 0 }}
            isLoading={isLoading}
            sortBy={sortBy}
            sortOrder={sortOrder}
            currentSiteId={currentSiteId}
            onRowClick={handleRowClick}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            onFetchAllRows={() =>
              fetchAllForExport(async ({ limit: exportLimit }) => {
                const response = await transfersService.list({
                  ...queryParams,
                  page: 1,
                  limit: exportLimit,
                });
                return {
                  items: response.data,
                  pagination: { total: response.pagination.total },
                };
              })
            }
          />
        )}
      </div>

      {/* Transfer Detail Drawer */}
      <TransferDetailDrawer
        transferId={drawer.resourceId}
        open={drawer.isOpen}
        onOpenChange={(open) => !open && drawer.close()}
        canShip={canShip}
        canReceive={canReceive}
        canDelete={canDelete}
      />

      {/* Create Transfer Modal */}
      <CreateTransferModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
      />
    </div>
  );
}
